import { Icon } from '@iconify/react';
import { useCallback, useEffect, useState } from 'react';
// material
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
// other icons
import searchFill from '@iconify/icons-eva/search-fill';
import roundFilterList from '@iconify/icons-ic/round-filter-list';
import { useDispatch } from 'react-redux';
import { MdUpdate } from 'react-icons/md';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { DateRangeStyled, RootStyle, SearchStyle } from '../Global/Styling';
// ---------------------------------------------------------
import 'react-date-range/dist/styles.css'; // main css file
import 'react-date-range/dist/theme/default.css'; // theme css file
import { fDate } from '../../utils/formatTime';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import {
  getCityData,
  getLoanTypeData,
  getSourceListData,
  getStatusList,
  getBankBranchData
} from '../../store/Slice/Global/GlobalSlice';

export default function DisburseToolbar({
  filterName,
  onFilterName,
  filterDatesRange,
  setFilterDatesRange,
  filterState,
  setFilterState,
  parent,
  selected,
  setFilterName
}) {
  const navigate = useNavigate();
  const [isOpenFilter, openFilter] = useState(false);

  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { CityDrop, LoanTypeDrop, StatusDrop, SourceDrop, BankDrop } = useGlobalSelector();

  const handleRemoveFilter = () => {
    setFilterDatesRange([
      {
        startDate: null,
        endDate: null,
        key: 'selection'
      }
    ]);
    setFilterState((prev) => ({
      ...prev,
      bankId: null,
      loanTypeId: null,
      city: null,
      cityId: null,
      statusId: null,
      statusName: ' '
    }));
    setFilterName('');
  };

  const getLoanMasterApi = useCallback(() => {
    const obj = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId
    };
    dispatch(getLoanTypeData(obj));
  }, [LoginData]);

  //  city api
  const handleGetState = useCallback(
    (optionVal) => {
      const data = {
        ...(optionVal && { stateId: optionVal }),
        companyId: LoginData?.companyId,
        userId: LoginData?.userId,
        searchLiteral: '',
        sortOrder: 'desc'
      };
      dispatch(getCityData(data)); // fetch city data
    },
    [LoginData?.companyId, LoginData?.userId] // dependencies
  );

  useEffect(() => {
    handleGetState(null); // city list
    getLoanMasterApi(); // loan master list
    dispatch(getStatusList(58)); // status list
    dispatch(getSourceListData()); // source list
  }, []);

  const handleBranchDetails = (bankValue) => {
    if (!bankValue?.value) return '';
    dispatch(getBankBranchData(bankValue?.value));
  };

  return (
    <>
      {isOpenFilter && (
        <Box px={3} pt={2}>
          <Grid container spacing={3}>
            <Grid item xs>
              <Select
                fullWidth
                size="small"
                value={filterState.loanTypeId ?? ' '}
                onChange={(e) => {
                  const v = e.target.value === ' ' ? null : e.target.value;
                  setFilterState((prev) => ({
                    ...prev,
                    loanTypeId: v
                  }));
                }}
              >
                <MenuItem value=" " disabled>
                  Select Product
                </MenuItem>
                {LoanTypeDrop?.map((i) => (
                  <MenuItem key={i.value} value={i.value}>
                    {i.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs>
              <Autocomplete
                id="combo-box-bank"
                options={BankDrop || []}
                size="small"
                getOptionLabel={(option) => option?.label ?? ''}
                renderOption={(props, option) => <li {...props}>{option?.label ?? ''}</li>}
                renderInput={(params) => (
                  <TextField {...params} fullWidth placeholder="Select Bank" />
                )}
                value={BankDrop?.find((item) => item.value === filterState.bankId) || null}
                onChange={(e, newValue) => {
                  handleBranchDetails(newValue);
                  setFilterState((prev) => ({
                    ...prev,
                    bankId: newValue ? newValue.value : null
                  }));
                }}
              />
            </Grid>

            <Grid item xs>
              <Autocomplete
                disablePortal
                id="combo-box-city"
                size="small"
                renderInput={(params) => (
                  <TextField {...params} fullWidth placeholder="Select City" />
                )}
                value={filterState.city ?? null}
                options={CityDrop || []}
                getOptionLabel={(option) => option?.label ?? ''}
                renderOption={(props, option) => <li {...props}>{option?.label ?? ''}</li>}
                onChange={(e, newValue) => {
                  setFilterState((prev) => ({
                    ...prev,
                    city: newValue ?? null,
                    cityId: newValue ? newValue.value : null
                  }));
                }}
              />
            </Grid>

            <Grid item xs>
              <Select
                fullWidth
                size="small"
                value={filterState.statusName ?? ' '}
                onChange={(e) => {
                  const name = e.target.value;
                  setFilterState((prev) => ({
                    ...prev,
                    statusName: name,
                    statusId: name ? 1 : null // statusId only used for UI
                  }));
                }}
              >
                <MenuItem value=" " disabled>
                  Select Status
                </MenuItem>
                {StatusDrop?.map((s) => (
                  <MenuItem key={s.value} value={s.label}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs="auto">
              <Tooltip title="Reset Filter">
                <IconButton
                  sx={{ padding: '0', paddingTop: '0.4rem', paddingBottom: '0.4rem' }}
                  onClick={() => {
                    setFilterDatesRange([
                      {
                        startDate: null,
                        endDate: null,
                        key: 'selection'
                      }
                    ]);
                    setFilterState((prev) => ({
                      ...prev,
                      bankId: null,
                      loanTypeId: null,
                      product: ' ',
                      productId: null,
                      city: null,
                      cityId: null,
                      status: ' ',
                      statusId: null,
                      statusName: null,
                      disburseStatusName: null
                    }));
                    setFilterName('');
                  }}
                >
                  <FilterListOffIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
      )}

      <RootStyle
        sx={{
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          p: { xs: 2, sm: 2.5 },
          bgcolor: '#ffffff'
        }}
      >
        <Box display="flex" gap={1.25} flexWrap="wrap" alignItems="center">
          <Button
            variant="outlined"
            disabled={!selected || selected.length < 1}
            onClick={() => {
              localStorage.setItem('selectedItem', JSON.stringify(selected));
              navigate(`/${parent}/update`);
            }}
            startIcon={<MdUpdate size={18} />}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              color: '#475569',
              borderColor: '#e2e8f0',
              bgcolor: '#ffffff',
              px: 1.75,
              py: 0.8,
              '&:hover': {
                borderColor: '#0284c7',
                bgcolor: 'rgba(2, 132, 199, 0.04)',
                color: '#0284c7'
              }
            }}
          >
            Update ({selected?.length || 0})
          </Button>
        </Box>

        <Box
          display="flex"
          alignItems="center"
          gap={1.5}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          <TextField
            size="small"
            value={filterName}
            onChange={onFilterName}
            placeholder="Search Application..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon={searchFill} style={{ color: '#94a3b8', fontSize: 18 }} />
                </InputAdornment>
              )
            }}
            sx={{
              width: { xs: '100%', sm: 260 },
              '& .MuiOutlinedInput-root': {
                bgcolor: '#fff',
                borderRadius: '10px',
                fontSize: '13px',
                '& fieldset': { borderColor: '#e2e8f0' },
                '&:hover fieldset': { borderColor: '#cbd5e1' },
                '&.Mui-focused fieldset': { borderColor: '#0284c7' }
              }
            }}
          />

          <Button
            variant="outlined"
            onClick={() => openFilter((prev) => !prev)}
            startIcon={<Icon icon={roundFilterList} />}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              color: isOpenFilter ? '#0284c7' : '#475569',
              borderColor: isOpenFilter ? '#0284c7' : '#e2e8f0',
              bgcolor: isOpenFilter ? 'rgba(2, 132, 199, 0.06)' : '#fff',
              height: 40,
              px: 2,
              '&:hover': {
                borderColor: '#0284c7',
                bgcolor: 'rgba(2, 132, 199, 0.06)',
                color: '#0284c7'
              }
            }}
          >
            Filters
          </Button>
        </Box>
      </RootStyle>
    </>
  );
}
